import React from 'react';
import { Link, Prompt, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  Button,
  Confirm,
  Container,
  Form,
  Menu,
  Message,
  Segment,
} from 'semantic-ui-react';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { useClient } from '@plone/volto/hooks/client/useClient';
import Toolbar from '@plone/volto/components/manage/Toolbar/Toolbar';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import DragDropList from '@plone/volto/components/manage/DragDropList/DragDropList';
import SelectWidget from '@plone/volto/components/manage/Widgets/SelectWidget';
import dragSVG from '@plone/volto/icons/drag.svg';
import backSVG from '@plone/volto/icons/back.svg';
import config from '@plone/volto/registry';
import { nextPosition, reorderItems } from './ordering';
import './management.css';

export async function managementRequest(path, method = 'GET', body) {
  const response = await fetch(`/_ai-agent${path}`, {
    method,
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const data = response.status === 204 ? null : await response.json();
  if (!response.ok) {
    const detail = Array.isArray(data?.detail)
      ? data.detail
          .map((item) => `${item.loc.slice(1).join('.')}: ${item.msg}`)
          .join('; ')
      : data?.detail;
    throw new Error(
      detail || 'The request could not be completed. Please try again.',
    );
  }
  return data;
}

export default function LinoManagement() {
  const { pathname } = useLocation();
  const isClient = useClient();
  const [allowed, setAllowed] = React.useState(false);
  const [skills, setSkills] = React.useState([]);
  const [groups, setGroups] = React.useState([]);
  const [tab, setTab] = React.useState('skills');
  const [editing, setEditing] = React.useState(null);
  const [draft, setDraft] = React.useState(null);
  const [dirty, setDirty] = React.useState(false);
  const [deleting, setDeleting] = React.useState(null);
  const [busy, setBusy] = React.useState(true);
  const [error, setError] = React.useState('');

  const refresh = async () => {
    const [s, g] = await Promise.all([
      managementRequest('/skills'),
      managementRequest('/skill-groups'),
    ]);
    setSkills(s.skills);
    setGroups(g.groups);
  };

  React.useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        if (config.settings.aiEditor.client === 'mock') {
          throw new Error('Skill management requires the agent connection.');
        }
        await managementRequest('/admin');
        const [s, g] = await Promise.all([
          managementRequest('/skills'),
          managementRequest('/skill-groups'),
        ]);
        if (active) {
          setAllowed(true);
          setSkills(s.skills);
          setGroups(g.groups);
        }
      } catch (err) {
        if (active) setError(err.message);
      } finally {
        if (active) setBusy(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, []);

  React.useEffect(() => {
    if (!dirty) return;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const run = async (action) => {
    setBusy(true);
    setError('');
    try {
      await action();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  const close = () => {
    setEditing(null);
    setDraft(null);
    setDirty(false);
  };
  const open = (item) =>
    run(async () => {
      const value = item
        ? tab === 'skills'
          ? await managementRequest(`/skills/${encodeURIComponent(item.name)}`)
          : { ...item }
        : {
            name: '',
            title: '',
            position: nextPosition(
              tab === 'skills'
                ? skills.filter((skill) => skill.group === null)
                : groups,
            ),
            ...(tab === 'skills'
              ? {
                  description: '',
                  agent_description: '',
                  content: '',
                  group: null,
                }
              : {}),
          };
      setEditing({ name: item?.name, kind: tab });
      setDraft(value);
      setDirty(false);
    });
  const update = (field, value) => {
    setDraft({
      ...draft,
      [field]: value,
      ...(field === 'group'
        ? {
            position: nextPosition(
              skills.filter((skill) => skill.group === value),
            ),
          }
        : {}),
    });
    setDirty(true);
  };
  const save = () =>
    run(async () => {
      const base = editing.kind === 'skills' ? '/skills' : '/skill-groups';
      await managementRequest(
        editing.name ? `${base}/${encodeURIComponent(editing.name)}` : base,
        editing.name ? 'PATCH' : 'POST',
        draft,
      );
      close();
      await refresh();
    });
  const remove = () =>
    run(async () => {
      const base = tab === 'skills' ? '/skills' : '/skill-groups';
      await managementRequest(
        `${base}/${encodeURIComponent(deleting.name)}`,
        'DELETE',
      );
      setDeleting(null);
      await refresh();
    });
  const isSkill = editing?.kind === 'skills';
  const move = (items, { source, destination }) => {
    if (busy) return;
    const reordered = reorderItems(items, source, destination);
    if (!reordered) return;
    const base = tab === 'skills' ? '/skills' : '/skill-groups';
    const names = new Set(items.map((item) => item.name));
    if (tab === 'skills')
      setSkills([
        ...skills.filter((item) => !names.has(item.name)),
        ...reordered,
      ]);
    else setGroups(reordered);
    run(async () => {
      try {
        for (const item of reordered) {
          if (
            items.find((original) => original.name === item.name).position !==
            item.position
          ) {
            await managementRequest(
              `${base}/${encodeURIComponent(item.name)}`,
              'PATCH',
              { position: item.position },
            );
          }
        }
      } finally {
        // Reflect the server's order even if only part of the update succeeded.
        await refresh();
      }
    });
  };
  const sections =
    tab === 'skills'
      ? [{ name: null, title: 'Ungrouped' }, ...groups].map((group) => ({
          ...group,
          items: skills.filter((skill) => skill.group === group.name),
        }))
      : [{ name: null, title: 'Groups', items: groups }];

  return (
    <div className="view-wrapper">
      <Helmet title="Lino — Skills" />
      <Prompt when={dirty} message="Discard your unsaved changes?" />
      <Container className="controlpanel">
        <Segment.Group raised>
          <Segment className="primary">
            <h1>Lino</h1>
          </Segment>
          {error && <Message negative role="alert" content={error} />}
          {!allowed && busy && (
            <Segment loading aria-label="Loading Lino administration" />
          )}
          {allowed && (
            <>
              <Menu secondary pointing>
                <Menu.Item
                  name="Skills"
                  active={tab === 'skills'}
                  disabled={!!editing || busy}
                  onClick={() => setTab('skills')}
                />
                <Menu.Item
                  name="Groups"
                  active={tab === 'groups'}
                  disabled={!!editing || busy}
                  onClick={() => setTab('groups')}
                />
              </Menu>
              <Segment>
                {editing ? (
                  <Form onSubmit={save}>
                    <h2>
                      {editing.name ? 'Edit' : 'New'}{' '}
                      {isSkill ? 'skill' : 'group'}
                    </h2>
                    <fieldset
                      disabled={busy}
                      style={{ border: 0, padding: 0, margin: 0 }}
                    >
                      <Form.Input
                        id="lino-title"
                        label="Title"
                        required
                        maxLength={120}
                        value={draft.title}
                        onChange={(_, { value }) => update('title', value)}
                      />
                      <Form.Input
                        id="lino-name"
                        label="Identifier"
                        required
                        pattern={'[A-Za-z0-9_\\-]+'}
                        value={draft.name}
                        onChange={(_, { value }) => update('name', value)}
                      />
                      <p>
                        Use letters, numbers, hyphens or underscores for the
                        identifier.
                      </p>
                      {isSkill && (
                        <>
                          <Form.TextArea
                            id="lino-description"
                            label="Description for editors"
                            required
                            rows={2}
                            value={draft.description}
                            onChange={(_, { value }) =>
                              update('description', value)
                            }
                          />
                          <Form.TextArea
                            id="lino-agent-description"
                            label="Description for the agent"
                            required
                            rows={2}
                            value={draft.agent_description}
                            onChange={(_, { value }) =>
                              update('agent_description', value)
                            }
                          />
                          <Form.TextArea
                            id="lino-content"
                            label="Instructions"
                            required
                            rows={12}
                            value={draft.content}
                            onChange={(_, { value }) =>
                              update('content', value)
                            }
                          />
                          <Form.Field>
                            <label htmlFor="field-group">Group</label>
                            <SelectWidget
                              id="group"
                              title="Group"
                              wrapped={false}
                              disabled={busy}
                              choices={[
                                ['', 'Ungrouped'],
                                ...groups.map((group) => [
                                  group.name,
                                  group.title,
                                ]),
                              ]}
                              value={draft.group}
                              placeholder="Ungrouped"
                              noValueOption={false}
                              isClearable={false}
                              onChange={(_, value) =>
                                update('group', value || null)
                              }
                            />
                          </Form.Field>
                        </>
                      )}
                      <Button primary type="submit" loading={busy}>
                        Save
                      </Button>
                      <Button type="button" onClick={close}>
                        Cancel
                      </Button>
                    </fieldset>
                  </Form>
                ) : (
                  <>
                    <Button primary disabled={busy} onClick={() => open(null)}>
                      Add {tab === 'skills' ? 'skill' : 'group'}
                    </Button>
                    <p role="status">
                      {busy
                        ? 'Saving…'
                        : 'Drag the handles to reorder. Changes are saved automatically.'}
                    </p>
                    {sections
                      .filter((section) => section.items.length > 0)
                      .map((section) => (
                        <React.Fragment key={section.name || 'ungrouped'}>
                          <h2 id={`lino-list-${section.name || tab}`}>
                            {section.title}
                          </h2>
                          <div inert={busy ? '' : undefined}>
                            <DragDropList
                              childList={section.items.map((item) => [
                                item.name,
                                item,
                              ])}
                              forwardedAriaLabelledBy={`lino-list-${section.name || tab}`}
                              onMoveItem={(result) =>
                                move(section.items, result)
                              }
                            >
                              {({ child: item, draginfo }) => (
                                <div
                                  className="lino-sortable-row"
                                  ref={draginfo.innerRef}
                                  {...draginfo.draggableProps}
                                  key={item.name}
                                >
                                  <span
                                    className="drag handle"
                                    {...draginfo.dragHandleProps}
                                    aria-label={`Move ${item.title}`}
                                  >
                                    <Icon name={dragSVG} size="18px" />
                                  </span>
                                  <div className="lino-sortable-description">
                                    <strong>{item.title}</strong>
                                    {item.description && (
                                      <p>{item.description}</p>
                                    )}
                                  </div>
                                  <div className="lino-sortable-actions">
                                    <Button
                                      size="small"
                                      disabled={busy}
                                      aria-label={`Edit ${item.title}`}
                                      onClick={() => open(item)}
                                    >
                                      Edit
                                    </Button>
                                    <Button
                                      size="small"
                                      disabled={busy}
                                      aria-label={`Delete ${item.title}`}
                                      onClick={() => setDeleting(item)}
                                    >
                                      Delete
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </DragDropList>
                          </div>
                        </React.Fragment>
                      ))}
                    {(tab === 'skills' ? skills : groups).length === 0 && (
                      <p>No {tab} yet.</p>
                    )}
                  </>
                )}
              </Segment>
            </>
          )}
          <Segment>
            <Link to="/controlpanel">Back to Site Setup</Link>
          </Segment>
        </Segment.Group>
      </Container>
      <Confirm
        open={!!deleting}
        header={`Delete ${deleting?.title || ''}?`}
        content={
          tab === 'groups'
            ? 'The skills in this group will become ungrouped.'
            : 'This skill will no longer be available in Lino.'
        }
        confirmButton={{ content: 'Delete', negative: true, disabled: busy }}
        cancelButton={{ content: 'Cancel', disabled: busy }}
        onCancel={() => !busy && setDeleting(null)}
        onConfirm={remove}
      />
      {isClient &&
        createPortal(
          <Toolbar
            pathname={pathname}
            hideDefaultViewButtons
            inner={
              <Link to="/controlpanel" className="item">
                <Icon name={backSVG} size="30px" title="Back to Site Setup" />
              </Link>
            }
          />,
          document.getElementById('toolbar'),
        )}
    </div>
  );
}
